/**
 * AgentsView — deploy and manage Claude agents from inside the app.
 *
 * This is the management surface for the `agents` collection: a grid of
 * deployed-agent cards (status, persona, cadence, last run, run count)
 * with per-card Run / Pause / Resume controls, a detail drawer showing
 * the run timeline, and a "Deploy agent" modal that launches a new agent
 * from a template. A deployed ticket-worker is the in-app face of the
 * ticket autopilot: same personas, surfaced as long-lived instances.
 *
 * Wired to the agents adapter (src/lib/db/agents.ts) — localStorage in
 * dev, Firestore in prod. Magenta-only accent; Lucide icons only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  Plus,
  X,
  Play,
  Pause,
  Zap,
  Clock,
  CalendarClock,
  Hand,
  Infinity as InfinityIcon,
  Activity,
  CircleDashed,
  Trash2,
  Sparkles,
  Radio,
  Timer,
  ShieldCheck,
  History,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { useOptionalToast } from '../components/ToastProvider';
import { CrmHygieneCard } from '../components/CrmHygieneCard';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  AGENT_TEMPLATES,
  agentTemplate,
  bootstrapAgentsIfEmpty,
  deployAgent,
  deleteAgent,
  runAgentOnce,
  setAgentState,
  subscribeAgents,
} from '../lib/db/agents';
import { AUTOPILOT_AGENTS, agentLabel } from '../lib/agent/ticketAutopilot';
import { describeNextRun } from '../lib/agent/workforce';
import { fmtNextRun, type UseAgentScheduler } from '../lib/agent/agentScheduler';
import * as argoWorker from '../lib/agent/argoWorker';
import { subscribeActivity } from '../lib/db/activity';
import { summarizeAgentRuns, type AgentRunSummary } from '../lib/agent/runHistory';
import type {
  ActivityEntry,
  AgentCadence,
  AgentRunState,
  AgentTemplateId,
  DeployedAgent,
} from '../lib/db/types';

// --- Presentation -----------------------------------------------------------

const STATE_META: Record<
  AgentRunState,
  { label: string; pill: string; icon: LucideIcon }
> = {
  running: { label: 'Running', pill: 'glass-pill glass-pill--green', icon: Activity },
  paused: { label: 'Paused', pill: 'glass-pill glass-pill--orange', icon: Pause },
  idle: { label: 'Idle', pill: 'glass-pill', icon: CircleDashed },
};

const CADENCE_META: Record<AgentCadence, { label: string; icon: LucideIcon }> = {
  manual: { label: 'Manual', icon: Hand },
  continuous: { label: 'Continuous', icon: InfinityIcon },
  hourly: { label: 'Hourly', icon: Clock },
  daily: { label: 'Daily', icon: CalendarClock },
};

const CADENCES: AgentCadence[] = ['manual', 'continuous', 'hourly', 'daily'];

function fmtRelative(iso: string): string {
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ===========================================================================

interface AgentsViewProps {
  scheduler?: UseAgentScheduler;
  /** Agent to open on arrival (from the ⌘K command palette). The nonce
   *  lets the same agent be re-focused twice in a row. */
  focus?: { id: string; nonce: number } | null;
}

export function AgentsView({ scheduler, focus = null }: AgentsViewProps) {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const actor = user?.displayName?.split(' ')[0] ?? 'You';

  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showDeploy, setShowDeploy] = useState(false);
  // agent_run rows from the append-only governance ledger — the receipts
  // that make each card's run history real rather than self-reported.
  const [ledgerRuns, setLedgerRuns] = useState<ActivityEntry[]>([]);
  // Re-evaluated every 30s so "Active now" decays honestly without new rows.
  const [nowTick, setNowTick] = useState(() => Date.now());
  const lastFocusNonce = useRef(0);

  useEffect(() => {
    let active = true;
    bootstrapAgentsIfEmpty().catch(() => undefined);
    const unsub = subscribeAgents((records) => {
      if (active) {
        setAgents([...records].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)));
      }
    });
    const unsubLedger = subscribeActivity((rows) => {
      if (active) setLedgerRuns(rows.filter((r) => r.event === 'agent_run'));
    });
    const timer = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => {
      active = false;
      unsub();
      unsubLedger();
      window.clearInterval(timer);
    };
  }, []);

  // Entity quick-jump: open the agent chosen in the ⌘K palette.
  useEffect(() => {
    if (!focus || focus.nonce === lastFocusNonce.current) return;
    lastFocusNonce.current = focus.nonce;
    setSelectedId(focus.id);
  }, [focus]);

  const selected = useMemo(
    () => agents.find((a) => a.id === selectedId) ?? null,
    [agents, selectedId],
  );

  const runningCount = useMemo(
    () => agents.filter((a) => a.state === 'running').length,
    [agents],
  );

  // Per-agent ledger receipts: recent runs, ledgered counts, active-now.
  const runHistory = useMemo(
    () => summarizeAgentRuns(ledgerRuns, { now: nowTick }),
    [ledgerRuns, nowTick],
  );
  const activeNowCount = useMemo(
    () => agents.filter((a) => runHistory[a.id]?.activeNow).length,
    [agents, runHistory],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 18px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <span className="t-eyebrow">Projects · Agents</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Deployed agents
            </h2>
            <p className="t-meta" style={{ margin: '4px 0 0', color: C.textMuted }}>
              Real governed workers: every run lands in the Decision Ledger, and
              every write goes through approvals.
              {runningCount > 0 && (
                <span style={{ color: C.green, fontWeight: 700 }}>
                  {' '}· {runningCount} running
                </span>
              )}
              {activeNowCount > 0 && (
                <span style={{ color: C.green, fontWeight: 700 }}>
                  {' '}· {activeNowCount} active now
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span
              className="glass-pill"
              title="Runs append agent_run entries to the tamper-evident activity ledger; proposed writes wait in the approvals inbox."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <ShieldCheck size={11} /> Governed · ledgered
            </span>
            {canMutate && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => setShowDeploy(true)}
              >
                <Plus size={13} /> Deploy agent
              </button>
            )}
          </div>
        </div>
      </GlassPanel>

      {scheduler && (
        <SchedulerPanel scheduler={scheduler} agents={agents} canMutate={canMutate} />
      )}

      {/* Governed CRM-hygiene superpower: worker-drafted, risk-clustered,
          reversible fixes routed through the existing approval pipeline. */}
      <CrmHygieneCard />


      {/* Grid */}
      {agents.length === 0 ? (
        <GlassPanel style={{ padding: '40px 32px' }}>
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              gap: 10,
              textAlign: 'center',
              maxWidth: 420,
              margin: '0 auto',
              color: C.textMuted,
            }}
          >
            <Bot size={26} strokeWidth={1.75} />
            <span className="t-body">No agents deployed yet.</span>
            {canMutate && (
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => setShowDeploy(true)}
              >
                <Plus size={13} /> Deploy your first agent
              </button>
            )}
          </div>
        </GlassPanel>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
            alignItems: 'start',
          }}
        >
          {agents.map((a) => (
            <AgentCard
              key={a.id}
              agent={a}
              history={runHistory[a.id]}
              canMutate={canMutate}
              actor={actor}
              autonomyOn={scheduler?.enabled ?? false}
              onOpen={() => setSelectedId(a.id)}
            />
          ))}
        </div>
      )}

      {selected && (
        <AgentDetail
          agent={selected}
          canMutate={canMutate}
          actor={actor}
          onClose={() => setSelectedId(null)}
          onDeleted={() => setSelectedId(null)}
        />
      )}

      {showDeploy && (
        <DeployModal
          createdBy={actor}
          onClose={() => setShowDeploy(false)}
          onDeployed={(id) => {
            setShowDeploy(false);
            setSelectedId(id);
          }}
        />
      )}
    </div>
  );
}

// --- Scheduler master switch ------------------------------------------------

/**
 * The global autonomy control for the Agents tab. Flipping the master switch
 * on lets the app-wide scheduler loop fire each running agent on its cadence
 * ('continuous' / 'hourly' / 'daily'); 'manual' agents never auto-run. Shows
 * how many agents are eligible, whether real work is routed to the hosted
 * Claude worker or simulated, and a live ticker of the most recent scheduled
 * runs so autonomy is visibly working.
 */
function SchedulerPanel({
  scheduler,
  agents,
  canMutate,
}: {
  scheduler: UseAgentScheduler;
  agents: DeployedAgent[];
  canMutate: boolean;
}) {
  const { enabled, toggle, recentEvents, busy } = scheduler;
  const workerConfigured = argoWorker.isConfigured();

  // Agents the scheduler will actually drive: running + a non-manual cadence.
  const eligible = agents.filter(
    (a) => a.state === 'running' && a.cadence !== 'manual',
  );

  return (
    <GlassPanel
      variant={enabled ? 'magenta' : 'subtle'}
      animate={false}
      style={{ padding: '14px 18px' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background: enabled
                ? 'linear-gradient(135deg, rgba(255,0,255,0.28), rgba(187,65,152,0.16))'
                : 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
            }}
          >
            <Radio size={17} strokeWidth={1.9} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span className="t-card-title" style={{ display: 'block', color: C.text }}>
              Autonomy
            </span>
            <span className="t-meta" style={{ color: C.textMuted }}>
              {enabled
                ? eligible.length > 0
                  ? `Scheduling ${eligible.length} agent${eligible.length === 1 ? '' : 's'} on cadence`
                  : 'On — deploy a running, non-manual agent to schedule work'
                : 'Off — agents only run when you click Run now'}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            className={workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'}
            title={
              workerConfigured
                ? 'Scheduled runs are routed to the real Claude worker.'
                : 'Simulation mode — set VITE_AGENT_API_URL to connect the real Claude worker.'
            }
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            {workerConfigured ? <Zap size={11} /> : <CircleDashed size={11} />}
            {workerConfigured ? 'Connected to Claude worker' : 'Simulation mode'}
          </span>
          {enabled && busy && (
            <span
              className="glass-pill glass-pill--green"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <Activity size={11} /> Working…
            </span>
          )}
          {canMutate && (
            <button
              type="button"
              className={enabled ? 'glass-btn glass-btn--primary' : 'glass-btn'}
              onClick={toggle}
            >
              {enabled ? (
                <>
                  <Pause size={13} /> Pause autonomy
                </>
              ) : (
                <>
                  <Play size={13} /> Enable autonomy
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Live ticker of recent scheduled runs */}
      {recentEvents.length > 0 && (
        <ol
          style={{
            listStyle: 'none',
            margin: '12px 0 0',
            padding: '12px 0 0',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gap: 6,
          }}
        >
          {recentEvents.map((e) => (
            <li
              key={`${e.agentId}-${e.at}`}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  marginTop: 7,
                  flex: '0 0 auto',
                  background: C.green,
                }}
              />
              <span className="t-meta" style={{ color: C.textMuted }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{e.agentName}</span> ·{' '}
                {e.text}
              </span>
            </li>
          ))}
        </ol>
      )}
    </GlassPanel>
  );
}

// --- Card -------------------------------------------------------------------

function AgentCard({
  agent,
  history,
  canMutate,
  actor,
  autonomyOn,
  onOpen,
}: {
  agent: DeployedAgent;
  /** Ledger receipts for this agent (recent runs, counts, active-now). */
  history?: AgentRunSummary;
  canMutate: boolean;
  actor: string;
  /** Whether the app-wide scheduler will actually drive this agent. */
  autonomyOn: boolean;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useOptionalToast();
  const state = STATE_META[agent.state];
  const cadence = CADENCE_META[agent.cadence];
  const StateIcon = state.icon;
  const CadenceIcon = cadence.icon;

  const guard = (fn: () => Promise<unknown>) => async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // Run now with visible feedback: per-agent spinner (`busy`), then an honest
  // toast reporting how many proposals the run queued for approval — or that
  // it ran clean. The card's run count + last-run stamp update automatically
  // via the agents subscription once `runAgentOnce` persists them.
  const runNow = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const result = await runAgentOnce(agent.id);
      const queued = result.queued;
      toast?.push({
        kind: 'success',
        text:
          queued > 0
            ? `${agent.name}: ran — ${queued} proposal${queued === 1 ? '' : 's'} queued for your approval`
            : `${agent.name}: ran clean — nothing needed`,
      });
    } catch (err) {
      toast?.push({
        kind: 'error',
        text: `${agent.name}: run failed`,
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassPanel
      variant={agent.state === 'running' ? 'magenta' : 'subtle'}
      animate={false}
      style={{ padding: 0, cursor: 'pointer' }}
    >
      <button
        type="button"
        className="glass-row"
        onClick={onOpen}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: 16,
          display: 'grid',
          gap: 10,
          background: 'transparent',
          border: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background:
                'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
            }}
          >
            <Bot size={17} strokeWidth={1.9} />
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              className="t-card-title"
              style={{ display: 'block', color: C.text }}
            >
              {agent.name}
            </span>
            <span
              className="t-meta"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <Sparkles size={11} /> {agentLabel(agent.persona)}
            </span>
          </span>
          {(busy || history?.activeNow) && (
            <span
              className="glass-pill glass-pill--green"
              title="A ledgered run landed within the last few minutes."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  background: C.green,
                  animation: 'pulse 1.6s ease-in-out infinite',
                }}
              />
              Active now
            </span>
          )}
          <span
            className={state.pill}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}
          >
            <StateIcon size={11} /> {state.label}
          </span>
        </div>

        {agent.description && (
          <p
            className="t-body"
            style={{
              margin: 0,
              color: C.textMuted,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {agent.description}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            color: C.textMuted,
          }}
        >
          <span
            className="t-meta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <CadenceIcon size={12} /> {cadence.label}
          </span>
          <span
            className="t-meta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Zap size={12} /> {agent.runCount} {agent.runCount === 1 ? 'run' : 'runs'}
          </span>
          <span className="t-meta">
            {agent.lastRunAt ? `Last run ${fmtRelative(agent.lastRunAt)}` : 'Never run'}
          </span>
          {(() => {
            const next = describeNextRun(agent, autonomyOn);
            return (
              <span
                className="t-meta"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  color: next.scheduled ? C.magenta : C.textMuted,
                  fontWeight: next.scheduled ? 600 : 400,
                }}
                title={
                  next.scheduled
                    ? 'The scheduler will run this agent automatically'
                    : 'Nothing runs this agent on its own right now — click Run now'
                }
              >
                <Timer size={12} /> {next.label}
              </span>
            );
          })()}
        </div>

        {/* Ledger receipts: this agent's most recent agent_run entries from
            the append-only activity ledger — proof the worker is real. */}
        {history && history.recent.length > 0 && (
          <div
            style={{
              display: 'grid',
              gap: 5,
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              className="t-meta"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                color: C.textMuted,
                fontWeight: 600,
              }}
            >
              <History size={11} /> Recent runs · {history.runCount} ledgered
            </span>
            {history.recent.slice(0, 2).map((r) => (
              <span
                key={r.at}
                className="t-meta"
                style={{
                  color: C.textMuted,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={r.text}
              >
                <span style={{ color: C.text, fontWeight: 600 }}>{fmtRelative(r.at)}</span>
                {' — '}
                {r.text}
              </span>
            ))}
          </div>
        )}

        {canMutate && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 2,
              paddingTop: 10,
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span
              role="button"
              tabIndex={busy ? -1 : 0}
              className="glass-btn glass-btn--primary"
              aria-disabled={busy}
              aria-label={`Run ${agent.name} now`}
              onClick={busy ? undefined : runNow}
              onKeyDown={(e) => {
                if (busy) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void runNow(e as unknown as React.MouseEvent);
                }
              }}
              style={{
                flex: 1,
                justifyContent: 'center',
                ...(busy ? { opacity: 0.6, cursor: 'default' } : null),
              }}
            >
              {busy ? (
                <>
                  <CircleDashed size={12} /> Running…
                </>
              ) : (
                <>
                  <Play size={12} /> Run now
                </>
              )}
            </span>
            <span
              role="button"
              tabIndex={0}
              className="glass-btn"
              aria-disabled={busy}
              onClick={guard(() =>
                setAgentState(
                  agent.id,
                  agent.state === 'running' ? 'paused' : 'running',
                  actor,
                ),
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void guard(() =>
                    setAgentState(
                      agent.id,
                      agent.state === 'running' ? 'paused' : 'running',
                      actor,
                    ),
                  )(e as unknown as React.MouseEvent);
                }
              }}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              {agent.state === 'running' ? (
                <>
                  <Pause size={12} /> Pause
                </>
              ) : (
                <>
                  <Play size={12} /> Resume
                </>
              )}
            </span>
          </div>
        )}
      </button>
    </GlassPanel>
  );
}

// --- Detail drawer ----------------------------------------------------------

function AgentDetail({
  agent,
  canMutate,
  actor,
  onClose,
  onDeleted,
}: {
  agent: DeployedAgent;
  canMutate: boolean;
  actor: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useOptionalToast();
  const state = STATE_META[agent.state];
  const cadence = CADENCE_META[agent.cadence];
  const tpl = agentTemplate(agent.template);
  const timeline = [...agent.timeline].reverse();

  const run = (fn: () => Promise<unknown>) => async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // Run now with visible feedback — mirrors the card's handler so the detail
  // drawer's button spins and reports its real queued-proposal count too.
  const runNow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await runAgentOnce(agent.id);
      const queued = result.queued;
      toast?.push({
        kind: 'success',
        text:
          queued > 0
            ? `${agent.name}: ran — ${queued} proposal${queued === 1 ? '' : 's'} queued for your approval`
            : `${agent.name}: ran clean — nothing needed`,
      });
    } catch (err) {
      toast?.push({
        kind: 'error',
        text: `${agent.name}: run failed`,
        detail: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteAgent(agent.id);
      onDeleted();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(6, 6, 12, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', height: '100%', overflowY: 'auto' }}
      >
        <GlassPanel
          variant="sharp"
          animate={false}
          style={{ padding: '24px 26px', minHeight: '100%' }}
        >
          <div
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}
          >
            <div style={{ minWidth: 0 }}>
              <span
                className={state.pill}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <state.icon size={11} /> {state.label}
              </span>
              <h2 className="t-section-title" style={{ margin: '8px 0 0' }}>
                {agent.name}
              </h2>
              <span
                className="t-meta"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4 }}
              >
                <Sparkles size={11} /> {agentLabel(agent.persona)} · {tpl.label}
              </span>
            </div>
            <button type="button" className="glass-btn" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          {agent.description && (
            <p className="t-body" style={{ margin: '16px 0 0', color: C.textMuted }}>
              {agent.description}
            </p>
          )}

          {/* Controls */}
          {canMutate && (
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={() => void runNow()}
                disabled={busy}
                aria-label={`Run ${agent.name} now`}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {busy ? (
                  <>
                    <CircleDashed size={13} /> Running…
                  </>
                ) : (
                  <>
                    <Play size={13} /> Run now
                  </>
                )}
              </button>
              <button
                type="button"
                className="glass-btn"
                onClick={run(() =>
                  setAgentState(
                    agent.id,
                    agent.state === 'running' ? 'paused' : 'running',
                    actor,
                  ),
                )}
                disabled={busy}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {agent.state === 'running' ? (
                  <>
                    <Pause size={13} /> Pause
                  </>
                ) : (
                  <>
                    <Play size={13} /> Resume
                  </>
                )}
              </button>
            </div>
          )}

          {/* Meta */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginTop: 18,
            }}
          >
            <Meta
              label="Cadence"
              value={`${cadence.label}`}
            />
            <Meta label="Next run" value={fmtNextRun(agent)} />
            <Meta label="Runs" value={`${agent.runCount}`} />
            <Meta
              label="Last run"
              value={agent.lastRunAt ? fmtRelative(agent.lastRunAt) : 'Never'}
            />
            <Meta label="Deployed by" value={agent.createdBy} />
          </div>

          {/* Timeline */}
          <div style={{ marginTop: 22 }}>
            <span className="t-eyebrow">Run history</span>
            <ol
              style={{
                listStyle: 'none',
                margin: '8px 0 0',
                padding: 0,
                display: 'grid',
                gap: 10,
              }}
            >
              {timeline.map((e) => (
                <li
                  key={e.id}
                  style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 99,
                      marginTop: 6,
                      flex: '0 0 auto',
                      background: e.kind === 'run' ? C.green : C.magenta,
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span className="t-body" style={{ color: C.text }}>
                      {e.text}
                    </span>
                    <span className="t-meta" style={{ display: 'block' }}>
                      {fmtRelative(e.at)} · {e.actor}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Danger zone */}
          {canMutate && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 22,
                paddingTop: 16,
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <button
                type="button"
                className="glass-btn"
                onClick={onDelete}
                disabled={busy}
                style={{ color: C.orangeWarm }}
              >
                <Trash2 size={13} /> Decommission agent
              </button>
            </div>
          )}
        </GlassPanel>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="t-eyebrow">{label}</span>
      <span className="t-body" style={{ display: 'block', marginTop: 2, color: C.text }}>
        {value}
      </span>
    </div>
  );
}

// --- Deploy modal -----------------------------------------------------------

function DeployModal({
  createdBy,
  onClose,
  onDeployed,
}: {
  createdBy: string;
  onClose: () => void;
  onDeployed: (id: string) => void;
}) {
  const [template, setTemplate] = useState<AgentTemplateId>('ticket-worker');
  const [name, setName] = useState('');
  const [persona, setPersona] = useState(agentTemplate('ticket-worker').persona);
  const [cadence, setCadence] = useState<AgentCadence>(
    agentTemplate('ticket-worker').defaultCadence,
  );
  const [description, setDescription] = useState(
    agentTemplate('ticket-worker').description,
  );
  const [busy, setBusy] = useState(false);

  // Picking a template pre-fills persona, cadence, and description.
  const pickTemplate = (id: AgentTemplateId) => {
    const tpl = agentTemplate(id);
    setTemplate(id);
    setPersona(tpl.persona);
    setCadence(tpl.defaultCadence);
    setDescription(tpl.description);
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const created = await deployAgent({
        name: name.trim() || agentTemplate(template).label,
        template,
        persona,
        cadence,
        description,
        createdBy,
      });
      onDeployed(created.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        background: 'rgba(6, 6, 12, 0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(600px, 100%)', marginTop: 24 }}
      >
        <GlassPanel variant="sharp" animate={false} style={{ padding: '24px 26px' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <h2 className="t-section-title" style={{ margin: 0 }}>
              Deploy agent
            </h2>
            <button type="button" className="glass-btn" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          {/* Template picker */}
          <span className="t-eyebrow" style={{ display: 'block', marginTop: 16 }}>
            Template
          </span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 8,
              marginTop: 8,
            }}
          >
            {AGENT_TEMPLATES.map((tpl) => {
              const active = tpl.id === template;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  className="glass-btn"
                  onClick={() => pickTemplate(tpl.id)}
                  style={{
                    display: 'block',
                    textAlign: 'left',
                    padding: '10px 12px',
                    height: 'auto',
                    border: `1px solid ${active ? 'rgba(255,0,255,0.45)' : 'rgba(255,255,255,0.10)'}`,
                    background: active
                      ? 'linear-gradient(135deg, rgba(255,0,255,0.20), rgba(187,65,152,0.12))'
                      : 'transparent',
                  }}
                >
                  <span
                    className="t-body"
                    style={{
                      display: 'block',
                      color: active ? '#fff' : C.text,
                      fontWeight: 600,
                    }}
                  >
                    {tpl.label}
                  </span>
                  <span
                    className="t-meta"
                    style={{ display: 'block', marginTop: 2, color: C.textMuted }}
                  >
                    {tpl.blurb}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">Name</span>
              <input
                className="glass-input"
                placeholder={agentTemplate(template).label}
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Persona</span>
                <select
                  className="glass-input"
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                >
                  {AUTOPILOT_AGENTS.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span className="t-eyebrow">Cadence</span>
                <select
                  className="glass-input"
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value as AgentCadence)}
                >
                  {CADENCES.map((c) => (
                    <option key={c} value={c}>
                      {CADENCE_META[c].label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label style={{ display: 'grid', gap: 4 }}>
              <span className="t-eyebrow">What it does</span>
              <textarea
                className="glass-input"
                rows={3}
                placeholder="Describe this agent's scope…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" className="glass-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="glass-btn glass-btn--primary"
              onClick={submit}
              disabled={busy}
            >
              <Bot size={13} /> Deploy
            </button>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
}
